import { useState, useRef, useEffect } from 'react';
import { cloneDeep, isEqual } from 'lodash';

import {
  PAGE_SIZE
} from 'app/utils/Constants';

const useDataSource = props => {
  const {
    getRowData,
    filterModel,
    dropDownOptions,
    defaultDropdownOption
  } = props;

  // results per page
  const perPage = PAGE_SIZE;

  const [state, setState] = useState({
    loading: false,
    loaded: false,
    option: {},
    error: null
  });

  const clearError = () => setState(state => ({ ...state, error: null }));

  // store filterModel in ref for getRows callback
  const filterModelRef = useRef(filterModel);
  useEffect(() => {
    filterModelRef.current = filterModel;
  }, [filterModel]);

  // cache params & rowData to return cached response if params haven't changed
  const rowDataRef = useRef(null);
  const lastPageRef = useRef(null);
  const lastFilterModelRef = useRef(filterModel);

  // provide method to force reload despite page & filters not changing
  // (needed to refresh the grid after adding a record)
  const forceReloadRef = useRef(false);
  const forceReload = () => {
    forceReloadRef.current = true;
  }

  // provide listener to parent component, trigger on rowData fetch
  const onRowDataRef = useRef(null);
  const onRowData = callback => {
    onRowDataRef.current = callback;
  }

  let selectedOption = {};

  const setOption = option => {
    // setState(state => { return { ...state, option: option } });
    selectedOption = option;
  }

  const calcLastRow = (rowData, page, perPage) => {
    let records = rowData.records || [];
    let lastRow = rowData.total || null;
    if (records.length < perPage) {
      lastRow = (perPage * (page - 1)) + records.length
    }

    return lastRow
  }

  const emptyFilter = filter => {
    for (let i in filter) { return false; }
    return true;
  }

  const cleanFilterModel = () => {
    // the filterModel creates empty filters by storing empty objects.
    // these empty objects might have filterClass and filterType set,
    // since the filter type can be toggled on empty filters,
    // but it won't have a value key set.
    // note this means undefined, not null, which should be treated as a value.
    // however none of these placeholder objects should be sent to the server.
    // the server will treat any filter object as an intended filter.
    // removing the placeholder objects here before submitting
    let model = cloneDeep(filterModelRef.current);
    for (let field in model) {
      model[field] = model[field].filter(filter => {
        return filter.value !== undefined;
      });
    }

    return model;
  }

  const getDataSource = {
    getRows: async params => {
      let page = Math.floor(params.startRow / perPage) + 1;

      // clean placeholder objects from filterModel.
      // this cleaned model will be sent to the server and stored for comparison
      // on subsequent calls
      // (only send request if meaningful filterModel change)
      let cleanedFilterModel = cleanFilterModel();

      // if the page or filters haven't changed, then return the cached response
      let samePage = page === lastPageRef.current;
      let sameFilters = isEqual(cleanedFilterModel, lastFilterModelRef.current);

      if (samePage && sameFilters && !forceReloadRef.current) {
        let rowData = rowDataRef.current;
        let page = lastPageRef.current;

        params.successCallback(rowData.records, calcLastRow(rowData, page, perPage));
        return;
      }

      lastPageRef.current = page;
      lastFilterModelRef.current = cleanedFilterModel;

      setState(currentState => ({ ...currentState, loading: true }));

      try {
        // reset forceReload flag
        forceReloadRef.current = false;

        let rowData = await getRowData({
          page,
          per_page: perPage,
          filter_model: cleanedFilterModel,
          sort_model: params.sortModel
        });

        rowDataRef.current = rowData;
        onRowDataRef.current(page, rowData);

        setState(state => ({
          ...state,
          loading: false,
          loaded: true
        }));

        params.successCallback(rowData.records, calcLastRow(rowData, page, perPage));

      } catch (error) {
        setState(() => ({
          loading: false,
          loaded: true,
          error
        }));

        params.failCallback(error);
      }
    }
  }

  // if getRowData isn't passed then assume client-side model is being used
  // and disable. AgGrid will use rowData from props instead
  if (!getRowData) {
    return {
      enabled: false
    }
  }

  return {
    enabled: true,
    loading: state.loading,
    loaded: state.loaded,
    onRowData,
    setOption,
    error: state.error,
    clearError,
    getDataSource,
    forceReload
  }
}

export default useDataSource;
